import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Injectable,
  Input,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { QRCodeComponent } from 'angularx-qrcode';
import { OtpInputComponent } from 'app/shared/components/otp-input/otp-input.component';
import { map } from 'rxjs/operators';

/** Ancho del QR en teléfono: se escanea con la cámara del propio equipo. */
export const MFA_QR_WIDTH_COMPACT = 180;
/** Ancho del QR de escritorio: se escanea con OTRO teléfono, al lado del texto. */
export const MFA_QR_WIDTH_REGULAR = 132;
/**
 * Lo que la caja del QR agrega alrededor del canvas: `p-3` (12px × 2) más el
 * borde de 1px × 2. La caja NO tiene tamaño fijo — mide contenido + esto —, así
 * que este número describe la caja, no la impone.
 */
export const MFA_QR_BOX_CHROME = 26;

/**
 * Única fuente del tamaño del QR y de la huella de su caja.
 *
 * Vive acá y no en un archivo propio porque el bloque del QR es de este
 * componente: el skeleton del paso 0 (que dibujan el modal y la ruta
 * `/mfa-setup`) tiene que medir EXACTAMENTE lo mismo que la caja final, y si
 * cada uno resolviera el ancho por su cuenta volverían a desalinearse en
 * silencio al primer cambio.
 *
 * El corte es 599.98px: el breakpoint `sm` de este repo es 600px
 * (`tailwind.config.js`), no el default de Tailwind.
 */
@Injectable({ providedIn: 'root' })
export class MfaQrSizeService {
  private readonly _compact = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 599.98px)')
      .pipe(map((state) => state.matches)),
    { initialValue: false }
  );

  /** Ancho que se le pide a `<qrcode>`. */
  readonly width = computed(() =>
    this._compact() ? MFA_QR_WIDTH_COMPACT : MFA_QR_WIDTH_REGULAR
  );

  /** Lado exterior de la caja del QR: lo que el skeleton tiene que reservar. */
  readonly boxSize = computed(() => this.width() + MFA_QR_BOX_CHROME);
}

/**
 * Paso «vincular la app» del enrolamiento MFA: QR + clave manual plegada +
 * campo OTP. Es PRESENTACIONAL puro — no conoce AuthService ni el dialog: el
 * host le pasa el `FormControl` del código y escucha `confirmed`.
 *
 * Existe para que el modal (`MfaSetupDialogComponent`) y la ruta `/mfa-setup`
 * compartan exactamente el mismo bloque y la ruta no quede desfasada.
 *
 * Seguridad: el código TOTP nunca sale de acá salvo por `confirmed`, y el
 * secreto no se loguea en ningún lado.
 */
@Component({
  selector: 'app-mfa-enroll-step',
  templateUrl: './mfa-enroll-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    TranslocoModule,
    QRCodeComponent,
    OtpInputComponent,
  ],
})
export class MfaEnrollStepComponent {
  /** Control del código TOTP. Lo posee el host (modal o ruta). */
  @Input({ required: true }) control!: FormControl;
  @Input() otpauthUri: string | null = null;
  @Input() secret: string | null = null;
  /** Clave i18n del error inline bajo el campo; `null` = sin error. */
  @Input() errorKey: string | null = null;

  /** Emite el código cuando el usuario completó los 6 dígitos. */
  @Output() readonly confirmed = new EventEmitter<string>();

  /**
   * Ancho del QR según el ancho de pantalla. Es un signal y no dos `<qrcode>`
   * duplicados detrás de un `@if`: duplicarlos duplica también el canvas y el
   * riesgo de que uno de los dos se desalinee del skeleton.
   */
  readonly qrWidth = inject(MfaQrSizeService).width;

  readonly showKey = signal(false);
  readonly keyCopied = signal(false);
  /** La escritura al portapapeles falló: la clave sigue a la vista. */
  readonly keyCopyFailed = signal(false);

  /**
   * `navigator.clipboard` no existe en contextos inseguros (http que no sea
   * localhost): ahí el botón de copiar directamente no se dibuja.
   */
  readonly canCopy =
    typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText;

  toggleKey(): void {
    this.showKey.update((open) => !open);
  }

  copyKey(): void {
    const secret = this.secret;
    if (!secret || !this.canCopy) {
      return;
    }

    this.keyCopyFailed.set(false);
    // Igual que los recovery codes: «Copiado» sólo si RESOLVIÓ, y sin loguear.
    navigator.clipboard.writeText(secret).then(() => {
      this.keyCopied.set(true);
      setTimeout(() => this.keyCopied.set(false), 2000);
    }, () => this.keyCopyFailed.set(true));
  }
}
